import {
    AlertTriangle,
    CheckCircle2,
    ChevronLeft,
    Copy,
    Info,
    LoaderCircle,
    Sparkles,
    Users,
} from 'lucide-react'
import React, { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './DuplicateTemplateScreen.module.css'

const parseActiveStore = (raw) => {
    try {
        return raw && raw.trim() !== '' ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

const formatValue = (component) => {
    if (!component) return '—'
    if (component.valueType === 'FLAT') {
        return `₹${Number(component.value || 0).toLocaleString('en-IN')}`
    }
    if (component.valueType === 'PERCENT_OF_BASIC') {
        return `${component.value}% of Basic`
    }
    if (component.valueType === 'PERCENT_OF_GROSS') {
        return `${component.value}% of Gross`
    }
    return 'Auto-computed'
}

// ── Component ─────────────────────────────────────────────────────────────────
const DuplicateTemplateScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { templateIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    // State from navigation
    const sourceTemplateName = location.state?.templateName || 'Template'
    const sourceComponents = Array.isArray(location.state?.activeComponents)
        ? location.state.activeComponents
        : []

    // ── Form state ───────────────────────────────────────────────
    const [newName, setNewName] = useState(`Copy of ${sourceTemplateName}`)
    const [nameError, setNameError] = useState(null)
    const [isSaving, setIsSaving] = useState(false)

    // Close name error after VAL_004 auto-suggestion
    const [errorClearTimer, setErrorClearTimer] = useState(null)

    useEffect(() => {
        return () => {
            if (errorClearTimer) clearTimeout(errorClearTimer)
        }
    }, [errorClearTimer])

    // ── Derived ──────────────────────────────────────────────────
    const earnings = sourceComponents
        .filter((c) => c.componentCategory === 'EARNING')
        .sort((a, b) => a.sortOrder - b.sortOrder)
    const deductions = sourceComponents
        .filter((c) => c.componentCategory === 'DEDUCTION')
        .sort((a, b) => a.sortOrder - b.sortOrder)

    const charCount = newName.length

    // ── Handlers ─────────────────────────────────────────────────
    const handleNameChange = (e) => {
        setNewName(e.target.value)
        if (nameError) setNameError(null)
    }

    // ── API call ─────────────────────────────────────────────────
    const duplicateTemplate = async () => {
        // Client-side validation
        if (!newName.trim()) {
            setNameError('Template name is required.')
            return
        }
        if (newName.trim().length > 200) {
            setNameError('Template name must not exceed 200 characters.')
            return
        }
        setNameError(null)

        if (!authCtx?.token || !activeStore?.storeIdentifier || !templateIdentifier) return

        try {
            setIsSaving(true)

            const response = await fetch(BASE_URL_API + 'duplicateSalaryStructureTemplate', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    sourceTemplateIdentifier: templateIdentifier,
                    newTemplateName: newName.trim(),
                    createdBy: authCtx?.authIdentifier || '',
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                const { newTemplate, componentsCopied = 0, warnings = [] } = respo.response || {}

                // Show partial-copy warnings
                if (Array.isArray(warnings) && warnings.length > 0) {
                    warnings.forEach((w) => toast.warning(w.message))
                }

                toast.success(
                    `Template duplicated with ${componentsCopied} component${componentsCopied !== 1 ? 's' : ''}.`
                )

                // Navigate directly to the new template's detail screen
                navigate(`/payroll/salary-structure/${newTemplate.templateIdentifier}`, {
                    replace: true,
                    state: {
                        templateName: newTemplate.templateName,
                    },
                })
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_004': {
                    // Duplicate name — show inline error + auto-fill suggested name
                    setNameError(`"${newName.trim()}" already exists. Trying suggested name…`)
                    if (respo.response?.suggestedName) {
                        setNewName(respo.response.suggestedName)
                        // Clear the error after a short delay so owner sees it
                        const timer = setTimeout(() => setNameError(null), 1500)
                        setErrorClearTimer(timer)
                    }
                    break
                }
                case 'VAL_001':
                    if (respo.message?.toLowerCase().includes('createdby')) {
                        toast.error('Session error. Please log out and back in.')
                    } else {
                        setNameError(respo.message)
                    }
                    break
                case 'VAL_002':
                    setNameError(respo.message)
                    break
                case 'COMP_004':
                    toast.error(respo.message || 'Source template not found.')
                    navigate(-1)
                    break
                default:
                    toast.error(respo?.message || 'Failed to duplicate template.')
                    break
            }
        } catch (error) {
            console.error('duplicateSalaryStructureTemplate error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    // ─────────────────────────────────────────────────────────────
    return (
        <div className={styles.page}>

            {/* ── Header ── */}
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={12} /> Duplicate Template</span>
                    <h1 className={styles.headerTitle}>Create a Copy</h1>
                </div>
            </header>

            <div className={styles.formWrap}>

                {/* ── Source info banner ── */}
                <div className={styles.sourceBanner}>
                    <Copy size={15} className={styles.sourceBannerIcon} />
                    <div>
                        <p className={styles.sourceBannerLabel}>Copying from</p>
                        <p className={styles.sourceBannerName}>{sourceTemplateName}</p>
                    </div>
                </div>

                {/* ── Source preview card ── */}
                {sourceComponents.length > 0 ? (
                    <div className={styles.previewCard}>
                        <div className={styles.previewHeader}>
                            <CheckCircle2 size={14} className={styles.previewHeaderIcon} />
                            <span>
                                {sourceComponents.length} component{sourceComponents.length !== 1 ? 's' : ''} will be copied
                            </span>
                        </div>

                        <div className={styles.previewList}>
                            {/* Earnings */}
                            {earnings.length > 0 && (
                                <>
                                    <p className={styles.previewGroupLabel}>
                                        <span className={styles.previewDot} style={{ background: '#16a34a' }} />
                                        EARNINGS
                                    </p>
                                    {earnings.map((c) => (
                                        <div key={c.componentIdentifier} className={styles.previewRow}>
                                            <span className={styles.previewCompName}>{c.componentName}</span>
                                            <span className={styles.previewCompValue}>{formatValue(c)}</span>
                                        </div>
                                    ))}
                                </>
                            )}

                            {/* Deductions */}
                            {deductions.length > 0 && (
                                <>
                                    <p className={`${styles.previewGroupLabel} ${styles.previewGroupLabelGap}`}>
                                        <span className={styles.previewDot} style={{ background: '#dc2626' }} />
                                        DEDUCTIONS
                                    </p>
                                    {deductions.map((c) => (
                                        <div key={c.componentIdentifier} className={styles.previewRow}>
                                            <span className={styles.previewCompName}>{c.componentName}</span>
                                            <span className={styles.previewCompValue}>{formatValue(c)}</span>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className={styles.noComponentsCard}>
                        <AlertTriangle size={14} className={styles.noComponentsIcon} />
                        <p>
                            This template has no active components. The duplicate will start empty — you can add components after creation.
                        </p>
                    </div>
                )}

                {/* ── New name field ── */}
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor="newTemplateName">
                        New Template Name <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="newTemplateName"
                        className={`${styles.textInput} ${nameError ? styles.textInputError : ''}`}
                        type="text"
                        maxLength={200}
                        value={newName}
                        onChange={handleNameChange}
                        placeholder="e.g. Mid-Level Stylist"
                        autoComplete="off"
                        spellCheck={false}
                    />
                    {charCount > 150 && (
                        <p className={styles.charCount}>{charCount}/200</p>
                    )}
                    {nameError && (
                        <p className={styles.fieldError}>
                            <AlertTriangle size={12} /> {nameError}
                        </p>
                    )}
                </div>

                {/* ── Info note ── */}
                <div className={styles.infoNote}>
                    <Info size={14} className={styles.infoNoteIcon} />
                    <p>
                        Staff assignments are <strong>not</strong> copied. The new template starts with zero assigned staff.
                        You can assign staff after reviewing the duplicate.
                    </p>
                </div>

                <div className={styles.infoNote} style={{ marginTop: '-4px' }}>
                    <Users size={14} className={styles.infoNoteIcon} />
                    <p>
                        Inactive (removed) components and version history are <strong>not</strong> copied — only active components transfer.
                    </p>
                </div>

                {/* ── Create button ── */}
                <button
                    type="button"
                    className={`${styles.saveBtn} ${isSaving ? styles.saveBtnDisabled : ''}`}
                    disabled={isSaving}
                    onClick={duplicateTemplate}
                >
                    {isSaving ? (
                        <><LoaderCircle size={16} className={styles.spin} /> Duplicating…</>
                    ) : (
                        <><Copy size={16} /> Create Duplicate →</>
                    )}
                </button>

            </div>
        </div>
    )
}

export default DuplicateTemplateScreen
